import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import { toUsdString } from 'pared/utils/number'

import { useDateFilter } from '../../dateFilter'
import { useGroupFilter } from '../../groupFilter'
import { useVariables } from '../../variables'
import { IApiDataType } from '../types'

interface IMetricInfoType {
  unit: string
  value: number
}

interface IMetricDataType {
  [key: string]: IMetricInfoType
}

interface IListFarwestWeekTrendDataNodeType {
  locationGroupId: number
  dateIndex: string
  metricSummaryData: IMetricDataType
}

const query = gql`
  query ListFarwestWeekTrendData(
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
  ) {
    trendLocationGroupMetricValuesByDateRangeNumber(
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
      iDateRangeNumber: 4
    ) {
      nodes {
        locationGroupId
        businessYear
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        dateIndex
        metricData
        metricSummaryData
      }
    }
  }
`

export const farwestCogsTrackerCaseCountsConfigs = {
  farwestCogsTrackerCaseCountsCaseCountItem: 'string',
  farwestCogsTrackerCaseCountsWeek5InPast: 'string',
  farwestCogsTrackerCaseCountsWeek4InPast: 'string',
  farwestCogsTrackerCaseCountsWeek3InPast: 'string',
  farwestCogsTrackerCaseCountsWeek2InPast: 'string',
  farwestCogsTrackerCaseCountsWeek1InPast: 'string',
} as const

interface IKpiInfoType {
  key: string
  itemName: string
  type: 'count' | 'dollar'
  decimal?: number
}

const formatData = (
  kpiInfo: Pick<IKpiInfoType, 'type' | 'decimal'>,
  metricData?: IMetricInfoType,
) => {
  if (!metricData) return '-'

  switch (kpiInfo.type) {
    case 'count':
      return Math.abs(metricData.value).toLocaleString('en-US', {
        minimumFractionDigits: kpiInfo.decimal || 0,
        maximumFractionDigits: kpiInfo.decimal || 0,
      })

    case 'dollar':
      return toUsdString(metricData.value / 100.0, kpiInfo.decimal)
  }
}

const KPIS_INFO: IKpiInfoType[] = [
  {
    key: 'bone_in_wings_case_cost',
    itemName: 'Bone-in wings - case cost',
    type: 'dollar',
    decimal: 2,
  },
  {
    key: 'bone_in_wings_cost_per_lb',
    itemName: 'Bone-in wings - Cost/lb',
    type: 'dollar',
    decimal: 2,
  },
  {
    key: 'bone_in_wings_count_per_case',
    itemName: 'Bone-in wings - Count per Case (Exclude <= 150 OR >= 300)',
    type: 'count',
  },
  {
    key: 'bone_in_wings_cost_per_wing',
    itemName: 'Bone-in wings - Cost Per Wing',
    type: 'dollar',
    decimal: 2,
  },
  {
    key: 'boneless_wings_case_cost',
    itemName: 'Boneless wings - case cost',
    type: 'dollar',
    decimal: 2,
  },
  {
    key: 'boneless_wings_cost_per_lb',
    itemName: 'Boneless wings - Cost/lb',
    type: 'dollar',
    decimal: 2,
  },
  {
    key: 'boneless_wings_count_per_case',
    itemName: 'Boneless wings - Count per Case (Exclude <= 300 OR >= 550)',
    type: 'count',
  },
  {
    key: 'boneless_wings_cost_per_wing',
    itemName: 'Boneless wings - Cost Per Wing',
    type: 'dollar',
    decimal: 2,
  },
  {
    key: 'chicken_tenders_case_cost',
    itemName: 'Chicken Tenders - case cost',
    type: 'dollar',
    decimal: 2,
  },
  {
    key: 'chicken_tenders_cost_per_lb',
    itemName: 'Chicken Tenders - Cost/lb',
    type: 'dollar',
    decimal: 2,
  },
  {
    key: 'chicken_tenders_count_per_case',
    itemName: 'Chicken Tenders - Count per Case (Exclude <= 100 OR >= 300)',
    type: 'count',
  },
  {
    key: 'chicken_tenders_cost_per_wing',
    itemName: 'Chicken Tenders - Cost Per Tender',
    type: 'dollar',
    decimal: 2,
  },
  {
    key: 'potatoes_case_cost',
    itemName: 'Potatoes - case cost',
    type: 'dollar',
    decimal: 2,
  },
  {
    key: 'frozen_fries_case_cost',
    itemName: 'Frozen Fries - case cost',
    type: 'dollar',
    decimal: 2,
  },
  {
    key: 'shortening_case_cost',
    itemName: 'Shortening - case cost',
    type: 'dollar',
    decimal: 2,
  },
  {
    key: 'lg_pp_blk_case_cost',
    itemName: 'Cont Large Polypropylene',
    type: 'dollar',
    decimal: 2,
  },
  {
    key: 'sandwich_pp_blk_case_cost',
    itemName: 'Cont Sandwich Polypropylene',
    type: 'dollar',
    decimal: 2,
  },
]

const useFarwestCogsTrackerCaseCounts = () => {
  const { endDate } = useDateFilter()
  const { groupFilter, hasGroupBy } = useGroupFilter()
  const { variables } = useVariables()

  const yesterday = moment().startOf('day').subtract(1, 'day')
  const adjustedEndDate =
    endDate && moment(endDate, 'YYYY-MM-DD').isAfter(yesterday)
      ? yesterday.format('YYYY-MM-DD')
      : endDate

  const dateType = variables.date?.value.type
  const dateRangeStartDate = variables.date?.getInfo(0)?.dateRange.startDate
  const iGroupBy = ((dateType?: string) => {
    if (dateType === 'period') {
      return 'last_x_periods'
    } else if (dateType === 'quarter') {
      return 'last_x_quarters'
    } else if (dateType === 'year') {
      return 'last_x_years'
    } else {
      return 'last_x_weeks'
    }
  })(dateType)

  const { data, loading } = useQuery(query, {
    variables: {
      iEndDate: adjustedEndDate,
      iGroupBy,
      iFilter: {
        location_group_ids: groupFilter?.ids,
        intersected_location_group_ids: groupFilter?.intersectedIds,
        metrics: KPIS_INFO.map((k) => k.key),
      },
      hasGroupBy,
    },
    skip: !adjustedEndDate || !groupFilter,
  })

  return {
    data: useMemo((): IApiDataType => {
      const nodeData: IListFarwestWeekTrendDataNodeType[] =
        data?.trendLocationGroupMetricValuesByDateRangeNumber?.nodes

      if (!nodeData) return null

      const customizedData = dateRangeStartDate
        ?.clone()
        .subtract(1, 'days')
        .isSame(yesterday)
        ? nodeData.map((d) => ({
            ...d,
            dateIndex: d.dateIndex + 1,
          }))
        : nodeData

      return {
        source: KPIS_INFO.map((kpi) => ({
          farwestCogsTrackerCaseCountsCaseCountItem: kpi.itemName,
          farwestCogsTrackerCaseCountsWeek5InPast: formatData(
            kpi,
            customizedData.find(
              (c) => c.dateIndex?.toString() === '5' && c.metricSummaryData,
            )?.metricSummaryData[kpi.key],
          ),
          farwestCogsTrackerCaseCountsWeek4InPast: formatData(
            kpi,
            customizedData.find(
              (c) => c.dateIndex?.toString() === '4' && c.metricSummaryData,
            )?.metricSummaryData[kpi.key],
          ),
          farwestCogsTrackerCaseCountsWeek3InPast: formatData(
            kpi,
            customizedData.find(
              (c) => c.dateIndex?.toString() === '3' && c.metricSummaryData,
            )?.metricSummaryData[kpi.key],
          ),
          farwestCogsTrackerCaseCountsWeek2InPast: formatData(
            kpi,
            customizedData.find(
              (c) => c.dateIndex?.toString() === '2' && c.metricSummaryData,
            )?.metricSummaryData[kpi.key],
          ),
          farwestCogsTrackerCaseCountsWeek1InPast: formatData(
            kpi,
            customizedData.find(
              (c) => c.dateIndex?.toString() === '1' && c.metricSummaryData,
            )?.metricSummaryData[kpi.key],
          ),
        })),
      }
    }, [groupFilter, data, variables]),
    loading,
  }
}

export default useFarwestCogsTrackerCaseCounts
